import { useMemo } from 'react';

interface PlayingCardProps {
  cardId: string; // Format: "rank_suit" e.g., "A_spades", "10_hearts", "K_clubs"
  faceDown?: boolean;
  selected?: boolean;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

const SUITS: Record<string, { symbol: string; color: string }> = {
  spades: { symbol: '♠', color: '#000000' },
  hearts: { symbol: '♥', color: '#DC2626' },
  diamonds: { symbol: '♦', color: '#DC2626' },
  clubs: { symbol: '♣', color: '#000000' },
  joker: { symbol: '🃏', color: '#7C3AED' }, // Purple for jokers
};


const SIZES = {
  sm: { width: 60, height: 84, fontSize: 14, symbolSize: 20 },
  md: { width: 80, height: 112, fontSize: 18, symbolSize: 28 },
  lg: { width: 100, height: 140, fontSize: 22, symbolSize: 36 },
};

export function PlayingCard({ cardId, faceDown = false, selected = false, onClick, size = 'md' }: PlayingCardProps) {
  const { rank, suit, isJoker, jokerType } = useMemo(() => {
    const parts = cardId.split('_');
    const r = parts[0];
    const s = parts[1];
    const isJkr = s === 'joker';
    return {
      rank: r,
      suit: s,
      isJoker: isJkr,
      jokerType: isJkr ? (r === 'BigJoker' ? 'Big' : 'Little') : null,
    };
  }, [cardId]);

  const suitInfo = SUITS[suit] || SUITS.spades;
  const dimensions = SIZES[size];

  if (faceDown) {
    return (
      <div
        className={`relative rounded-lg shadow-lg cursor-pointer transition-transform ${selected ? 'ring-2 ring-yellow-400 -translate-y-2' : 'hover:-translate-y-1'}`}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
          border: '2px solid #334155',
        }}
        onClick={onClick}
      >
        {/* Card back pattern */}
        <div className="absolute inset-2 rounded border border-slate-600 opacity-50">
          <div className="w-full h-full flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-slate-500" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // Special rendering for jokers
  if (isJoker) {
    const jokerColor = jokerType === 'Big' ? '#DC2626' : '#000000'; // Big = red, Little = black
    return (
      <div
        className={`relative bg-white rounded-lg shadow-lg cursor-pointer transition-transform ${selected ? 'ring-2 ring-yellow-400 -translate-y-2' : 'hover:-translate-y-1'}`}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          border: '2px solid #7C3AED',
          background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
        }}
        onClick={onClick}
      >
        {/* Top-left joker label */}
        <div className="absolute top-1 left-1 flex flex-col items-center leading-none" style={{ color: jokerColor }}>
          <span style={{ fontSize: dimensions.fontSize * 0.7, fontWeight: 'bold' }}>
            {jokerType === 'Big' ? 'BIG' : 'LIL'}
          </span>
          <span style={{ fontSize: dimensions.symbolSize }}>🃏</span>
        </div>

        {/* Center joker */}
        <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ color: jokerColor }}>
          <span style={{ fontSize: dimensions.symbolSize * 1.8 }}>🃏</span>
          <span style={{ fontSize: dimensions.fontSize * 0.6, fontWeight: 'bold' }}>JOKER</span>
        </div>

        {/* Bottom-right (inverted) */}
        <div className="absolute bottom-1 right-1 flex flex-col items-center leading-none rotate-180" style={{ color: jokerColor }}>
          <span style={{ fontSize: dimensions.fontSize * 0.7, fontWeight: 'bold' }}>
            {jokerType === 'Big' ? 'BIG' : 'LIL'}
          </span>
          <span style={{ fontSize: dimensions.symbolSize }}>🃏</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative bg-white rounded-lg shadow-lg cursor-pointer transition-transform ${selected ? 'ring-2 ring-yellow-400 -translate-y-2' : 'hover:-translate-y-1'}`}
      style={{
        width: dimensions.width,
        height: dimensions.height,
        border: '1px solid #e5e7eb',
      }}
      onClick={onClick}
    >
      {/* Top-left rank and suit */}
      <div className="absolute top-1 left-1 flex flex-col items-center leading-none" style={{ color: suitInfo.color }}>
        <span style={{ fontSize: dimensions.fontSize, fontWeight: 'bold' }}>{rank}</span>
        <span style={{ fontSize: dimensions.symbolSize }}>{suitInfo.symbol}</span>
      </div>

      {/* Center suit */}
      <div className="absolute inset-0 flex items-center justify-center" style={{ color: suitInfo.color }}>
        <span style={{ fontSize: dimensions.symbolSize * 1.5 }}>{suitInfo.symbol}</span>
      </div>

      {/* Bottom-right rank and suit (inverted) */}
      <div className="absolute bottom-1 right-1 flex flex-col items-center leading-none rotate-180" style={{ color: suitInfo.color }}>
        <span style={{ fontSize: dimensions.fontSize, fontWeight: 'bold' }}>{rank}</span>
        <span style={{ fontSize: dimensions.symbolSize }}>{suitInfo.symbol}</span>
      </div>
    </div>
  );
}

export function CardHand({
  cards,
  selectedCards = [],
  onCardClick,
  faceDown = false,
  size = 'md',
  spread = true
}: {
  cards: string[];
  selectedCards?: string[];
  onCardClick?: (cardId: string) => void;
  faceDown?: boolean;
  size?: 'sm' | 'md' | 'lg';
  spread?: boolean;
}) {
  const dimensions = SIZES[size];
  const overlap = spread ? dimensions.width * 0.4 : dimensions.width * 0.8;

  return (
    <div className="flex" style={{ marginLeft: overlap / 2 }}>
      {cards.map((cardId, index) => (
        <div
          key={cardId}
          style={{ marginLeft: index === 0 ? 0 : -overlap }}
        >
          <PlayingCard
            cardId={cardId}
            faceDown={faceDown}
            selected={selectedCards.includes(cardId)}
            onClick={() => onCardClick?.(cardId)}
            size={size}
          />
        </div>
      ))}
    </div>
  );
}
